import type React from 'react';
import { create } from 'zustand';
import { getState, useState } from '@/framework';
import type { IExportState, StateType } from '@/version-types';
import { getBuiltinStageToolbarItems } from '../builtin';
import { DEFAULT_STAGE_TOOLBAR_CONFIG } from '../config';
import type { IStageToolbarConfig, IStageToolbarItem, StageToolbarItemKey } from '../types';

interface IState {
    visible: boolean;
    config: IStageToolbarConfig;
    items: IStageToolbarItem[];
    itemAvailability: Record<string, boolean>;
    activeItemKey: StageToolbarItemKey | null;
    customNode: React.ReactNode;
    referenceLineVisible: boolean;
    hierarchyPanelPosition: React.CSSProperties;
}

interface IAction {
    setVisible(visible: boolean): void;
    setConfig(config: IStageToolbarConfig): void;
    setItems(items: IStageToolbarItem[]): void;
    setItemAvailability(key: StageToolbarItemKey, available: boolean): void;
    setActiveItem(key: StageToolbarItemKey | null): void;
    setCustomNode(node: React.ReactNode): void;
    setReferenceLineVisible(visible: boolean): void;
    setHierarchyPanelPosition(position: React.CSSProperties): void;
}

interface IStore extends IState, IAction {}

// framework 会按编辑器 identity 调用 createState；状态不得提升为跨编辑器共享的模块级 store。
const createState = (_: string) =>
    create<IStore>((set) => ({
        visible: true,
        config: DEFAULT_STAGE_TOOLBAR_CONFIG,
        items: getBuiltinStageToolbarItems(),
        itemAvailability: {},
        activeItemKey: null,
        customNode: null,
        referenceLineVisible: true,
        hierarchyPanelPosition: {},

        setVisible(visible) {
            set({ visible });
        },
        setConfig(config) {
            // 整体换肤/换形态时关闭当前浮层，防止活动条目已被新配置移除。
            set({ config, activeItemKey: null });
        },
        setItems(items) {
            set({ items });
        },
        setItemAvailability(key, available) {
            set((state) => ({
                itemAvailability: {
                    ...state.itemAvailability,
                    [key]: available,
                },
                // 运行时禁用活动条目时同步收起其浮层，保持 activeItemKey 与可用性一致。
                activeItemKey: !available && state.activeItemKey === key ? null : state.activeItemKey,
            }));
        },
        setActiveItem(activeItemKey) {
            set({ activeItemKey });
        },
        setCustomNode(customNode) {
            set({ customNode });
        },
        setReferenceLineVisible(referenceLineVisible) {
            set({ referenceLineVisible });
        },
        setHierarchyPanelPosition(hierarchyPanelPosition) {
            set({ hierarchyPanelPosition });
        },
    }));

const name = 'stage-toolbar-state';
type ModuleType = StateType<IStore, typeof name>;

export const useStageToolbarState = () => useState<ModuleType>()[name];
export const getStageToolbarState = (identity: string) => getState<ModuleType>(identity)[name].getState();

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
